import { useEffect, useState } from 'react'
import { useSetRecoilState } from 'recoil'
import { caseAtom, pageLoadingAtom, applicationTypeAtom } from 'atoms'
import { caseImagesAtom } from '@/atoms/caseImageAtoms'
import { PageLoading } from 'components/page'
import { EditUnauthorized } from 'components/landing'
import { useSetToast } from 'hooks'
import regEx from 'utils/global/regEx'
import { useTranslation } from 'react-i18next'
import {
  EDIT_TOKEN_ROLE,
  EDIT_TOKEN_STORAGE_KEYS,
  LEGACY_QC_SHADOW_ACCESS,
  M3,
  MDL,
} from '@messenger/consts'
import { getCase } from '@/api/cases'
import { getCaseImageList } from '@/api/caseImages'
import { getOrder, validateEditToken } from '@/api/orders'
import {
  clearOrderSession,
  storeOrderSession,
  tabOrderId,
} from '@/utils/global/orderSession'

export const extractUUID = (path) => {
  const match = path.match(regEx.authUUID)
  return match ? match[1] : null
}

export const Auth = () => {
  const setCaseInfo = useSetRecoilState(caseAtom)
  const setCaseImages = useSetRecoilState(caseImagesAtom)
  const setPageLoading = useSetRecoilState(pageLoadingAtom)
  const setApplicationType = useSetRecoilState(applicationTypeAtom)
  const setToast = useSetToast()
  const { t } = useTranslation()
  const [showUnauthorized, setShowUnauthorized] = useState(false)
  const [returnUrl, setReturnUrl] = useState('/')
  const [shouldCloseTab, setShouldCloseTab] = useState(false)

  useEffect(() => {
    const location = window.location.pathname
    const messengerOrderIdTokenFromURL = extractUUID(location)

    const checkIfOrderExists = async () => {
      let isM3 = false
      if (!messengerOrderIdTokenFromURL) {
        setPageLoading(false)
        window.location.href = '/'
        return
      }

      try {
        setPageLoading(true)

        // A previous session's token grants must never bleed into this one.
        sessionStorage.removeItem(EDIT_TOKEN_STORAGE_KEYS.role)
        sessionStorage.removeItem(EDIT_TOKEN_STORAGE_KEYS.scopeOrderProductId)
        sessionStorage.removeItem(EDIT_TOKEN_STORAGE_KEYS.token)

        // Check for edit token - now required
        const urlParams = new URLSearchParams(window.location.search)
        const editToken = urlParams.get('token')
        const accessType = urlParams.get('access')

        // Check if this is M3 (has m3OrderProductId parameter)
        isM3 = urlParams.has('m3OrderProductId')

        // Check for QC shadow access - bypasses token validation
        const isQcShadowAccess = accessType === LEGACY_QC_SHADOW_ACCESS

        if (!editToken && !isQcShadowAccess) {
          // No token provided and not QC shadow access, show edit-unauthorized
          clearOrderSession()
          setPageLoading(false)
          setReturnUrl(isM3 ? null : '/')
          setShouldCloseTab(isM3)
          setShowUnauthorized(true)
          return
        }

        // Only validate token if not QC shadow access
        if (!isQcShadowAccess) {
          try {
            // Validate edit token
            const tokenResponse = await validateEditToken(editToken)

            if (tokenResponse.data.error || !tokenResponse.data.valid) {
              // Token is invalid, show edit-unauthorized with return_url
              clearOrderSession()
              setPageLoading(false)
              setReturnUrl(isM3 ? null : tokenResponse.data.returnUrl || '/')
              setShouldCloseTab(isM3)
              setShowUnauthorized(true)
              return
            }

            // Token is valid — the order it grants comes from the token
            // binding itself, never from the URL path. A token that binds
            // to no order grants nothing.
            const orderIdToStore = tokenResponse.data.isM3
              ? tokenResponse.data.m3OrderId
              : tokenResponse.data.orderId
            if (!orderIdToStore) {
              clearOrderSession()
              setPageLoading(false)
              setReturnUrl(isM3 ? null : tokenResponse.data.returnUrl || '/')
              setShouldCloseTab(isM3)
              setShowUnauthorized(true)
              return
            }

            storeOrderSession(orderIdToStore)
            // Store session validation flag to prevent bypass
            sessionStorage.setItem('tokenValidated', 'true')
            sessionStorage.setItem('tokenValidatedAt', Date.now().toString())
            if (tokenResponse.data.role) {
              sessionStorage.setItem(
                EDIT_TOKEN_STORAGE_KEYS.role,
                tokenResponse.data.role,
              )
            } else {
              sessionStorage.removeItem(EDIT_TOKEN_STORAGE_KEYS.role)
            }
            // Designer and QC sessions keep the raw token: it goes beside
            // x-order on every request, so the backend can read the session's
            // role from the token, and it authenticates a designer's
            // send-proof calls.
            if (
              tokenResponse.data.role === EDIT_TOKEN_ROLE.DESIGNER ||
              tokenResponse.data.role === EDIT_TOKEN_ROLE.QC
            ) {
              sessionStorage.setItem(EDIT_TOKEN_STORAGE_KEYS.token, editToken)
            }
            if (tokenResponse.data.scopeOrderProductId) {
              sessionStorage.setItem(
                EDIT_TOKEN_STORAGE_KEYS.scopeOrderProductId,
                tokenResponse.data.scopeOrderProductId,
              )
            } else {
              sessionStorage.removeItem(
                EDIT_TOKEN_STORAGE_KEYS.scopeOrderProductId,
              )
            }
          } catch (error) {
            // Error validating token, show edit-unauthorized
            clearOrderSession()
            setPageLoading(false)
            setReturnUrl(isM3 ? null : error?.response?.data?.returnUrl || '/')
            setShouldCloseTab(isM3)
            setShowUnauthorized(true)
            return
          }
        } else {
          // QC shadow access - set order ID directly without token validation
          storeOrderSession(messengerOrderIdTokenFromURL)
          // Mark as QC shadow access in session
          sessionStorage.setItem('qcShadowAccess', 'true')
        }

        // Verify that the token is set
        const storedTokenInLocalStorage = tabOrderId()
        let orderResponse = null
        if (storedTokenInLocalStorage) {
          // Extract query parameters from URL
          const queryParams = {}

          // Add m3ProductId if present in URL
          if (urlParams.get('m3OrderProductId')) {
            queryParams.m3OrderProductId = urlParams.get('m3OrderProductId')
            isM3 = true
            setApplicationType(M3) // Set application type to M3
          } else {
            setApplicationType(MDL) // Explicitly set to MDL if no m3OrderProductId
          }

          // Call getOrder with query parameters if any exist
          orderResponse = await getOrder(storedTokenInLocalStorage, queryParams)
        } else {
          console.error('Failed to store the order id of this edit session')
        }

        if (orderResponse.status === 200) {
          const caseResponse = await getCase(
            orderResponse.data.case_id,
            isM3,
            isM3 ? orderResponse.data.id : undefined,
          )
          setCaseInfo(caseResponse.data)
          try {
            const caseImages = await getCaseImageList(caseResponse.data.id)
            setCaseImages(caseImages)
          } catch (err) {
            setCaseImages([])
            console.warn('Failed to load case images', {
              caseId: caseResponse.data.id,
              error: err,
            })
          }

          const currentUrlParams = new URLSearchParams(window.location.search)
          const newUrlParams = new URLSearchParams()

          currentUrlParams.forEach((value, key) => {
            // Don't pass the token parameter to the preview page
            if (key !== 'token') {
              newUrlParams.append(key, value)
            }
          })

          // Add a fake loading delay
          setTimeout(() => {
            setPageLoading(false)
            const previewUrl = `/preview/${storedTokenInLocalStorage}`
            const queryString = newUrlParams.toString()
            window.location.href = queryString
              ? `${previewUrl}?${queryString}`
              : previewUrl
          }, 1000)
        } else {
          throw new Error('Order does not exist')
        }
      } catch (error) {
        setToast({ text: t('toast.orderValidationProblem') })
        console.error('Error:', error.message)

        setTimeout(() => {
          clearOrderSession()
          setPageLoading(false)
          window.location.href = '/'
        }, 2000)
      }
    }

    checkIfOrderExists()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCaseInfo, setCaseImages, setPageLoading])

  if (showUnauthorized) {
    return (
      <EditUnauthorized
        returnUrlProp={returnUrl}
        shouldCloseTab={shouldCloseTab}
      />
    )
  }

  return (
    <>
      <PageLoading />
    </>
  )
}

export default Auth
